// hooks/useAppReducer.ts
// Unified state management for App component using useReducer

import { useReducer, Reducer } from 'react'

// ============================================================================
// State Shape
// ============================================================================

export interface AppState {
  // User selection flow
  userFlow: {
    selectedLanguage: string | null
    selectedAvatar: string | null
    selectedVariantIntro: string | null
  }
  // Video player state
  videoPlayer: {
    currentIndex: number
    isPaused: boolean
    sourcePath: string
  }
  // Contract selection
  contract: {
    contractType: string
  }
}

// ============================================================================
// Action Types
// ============================================================================

export enum ActionType {
  SET_LANGUAGE = 'SET_LANGUAGE',
  SET_AVATAR = 'SET_AVATAR',
  SET_VARIANT_INTRO = 'SET_VARIANT_INTRO',
  SET_VIDEO_INDEX = 'SET_VIDEO_INDEX',
  TOGGLE_PAUSE = 'TOGGLE_PAUSE',
  SET_PAUSE_STATE = 'SET_PAUSE_STATE',
  SET_CONTRACT_TYPE = 'SET_CONTRACT_TYPE',
  SET_SOURCE_PATH = 'SET_SOURCE_PATH',
  RESET_AVATAR = 'RESET_AVATAR'
}

// ============================================================================
// Action Interfaces
// ============================================================================

interface SetLanguageAction {
  type: ActionType.SET_LANGUAGE
  payload: string
}

interface SetAvatarAction {
  type: ActionType.SET_AVATAR
  payload: string
}

interface SetVariantIntroAction {
  type: ActionType.SET_VARIANT_INTRO
  payload: string | null
}

interface SetVideoIndexAction {
  type: ActionType.SET_VIDEO_INDEX
  payload: number
}

interface TogglePauseAction {
  type: ActionType.TOGGLE_PAUSE
}

interface SetPauseStateAction {
  type: ActionType.SET_PAUSE_STATE
  payload: boolean
}

interface SetContractTypeAction {
  type: ActionType.SET_CONTRACT_TYPE
  payload: string
}

interface SetSourcePathAction {
  type: ActionType.SET_SOURCE_PATH
  payload: string
}

interface ResetAvatarAction {
  type: ActionType.RESET_AVATAR
}

type AppAction =
  | SetLanguageAction
  | SetAvatarAction
  | SetVariantIntroAction
  | SetVideoIndexAction
  | TogglePauseAction
  | SetPauseStateAction
  | SetContractTypeAction
  | SetSourcePathAction
  | ResetAvatarAction

// ============================================================================
// Reducer Function
// ============================================================================

const appReducer: Reducer<AppState, AppAction> = (state, action) => {
  switch (action.type) {
    case ActionType.SET_LANGUAGE:
      return {
        ...state,
        userFlow: {
          ...state.userFlow,
          selectedLanguage: action.payload
        }
      }

    case ActionType.SET_AVATAR:
      return {
        ...state,
        userFlow: {
          ...state.userFlow,
          selectedAvatar: action.payload
        }
      }

    case ActionType.SET_VARIANT_INTRO:
      return {
        ...state,
        userFlow: {
          ...state.userFlow,
          selectedVariantIntro: action.payload
        }
      }

    case ActionType.SET_VIDEO_INDEX:
      return {
        ...state,
        videoPlayer: {
          ...state.videoPlayer,
          currentIndex: action.payload
        }
      }

    case ActionType.TOGGLE_PAUSE:
      return {
        ...state,
        videoPlayer: {
          ...state.videoPlayer,
          isPaused: !state.videoPlayer.isPaused
        }
      }

    case ActionType.SET_PAUSE_STATE:
      return {
        ...state,
        videoPlayer: {
          ...state.videoPlayer,
          isPaused: action.payload
        }
      }

    case ActionType.SET_CONTRACT_TYPE:
      return {
        ...state,
        contract: {
          ...state.contract,
          contractType: action.payload
        }
      }

    case ActionType.SET_SOURCE_PATH:
      return {
        ...state,
        videoPlayer: {
          ...state.videoPlayer,
          sourcePath: action.payload
        }
      }

    case ActionType.RESET_AVATAR:
      return {
        ...state,
        userFlow: {
          ...state.userFlow,
          selectedAvatar: null
        }
      }

    default:
      return state
  }
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: AppState = {
  userFlow: {
    selectedLanguage: null,
    selectedAvatar: null,
    selectedVariantIntro: null
  },
  videoPlayer: {
    currentIndex: 0,
    isPaused: false,
    sourcePath: ''
  },
  contract: {
    contractType: ''
  }
}

// ============================================================================
// Custom Hook
// ============================================================================

export function useAppReducer() {
  const [state, dispatch] = useReducer(appReducer, initialState)

  return {
    state,
    dispatch,
    // Convenience selectors
    selectors: {
      selectedLanguage: state.userFlow.selectedLanguage,
      selectedAvatar: state.userFlow.selectedAvatar,
      selectedVariantIntro: state.userFlow.selectedVariantIntro,
      currentIndex: state.videoPlayer.currentIndex,
      isPaused: state.videoPlayer.isPaused,
      sourcePath: state.videoPlayer.sourcePath,
      contractType: state.contract.contractType
    }
  }
}
